import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import {
  CheckCircle2, XCircle, Download, ChevronDown, ChevronUp,
  User, GraduationCap, BookOpen, Activity, Users, FolderOpen,
  Loader2, FileCheck, AlertTriangle, Sparkles, ArrowRight
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  profile: User,
  writing: BookOpen,
  activities: Activity,
  recommendations: Users,
  schools: GraduationCap,
  documents: FolderOpen,
};

const CATEGORY_LINKS = {
  profile: '/profile',
  writing: '/essays',
  activities: '/activities',
  recommendations: '/recommenders',
  schools: '/colleges',
  documents: '/documents',
};

const READINESS_STYLES = {
  green: { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-700', ring: '#16a34a' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-700', ring: '#2563eb' },
  amber: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-700', ring: '#d97706' },
  red: { bg: 'bg-red-50', border: 'border-red-300', text: 'text-red-700', ring: '#dc2626' },
};

function ReadinessRing({ pct, size = 96, stroke = 7, color }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#F2F2F0" strokeWidth={stroke} />
      <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        className="transition-all duration-1000 ease-out" />
    </svg>
  );
}

function CheckItem({ item, showLink = true }) {
  const Icon = CATEGORY_ICONS[item.category] || FileCheck;
  return (
    <div className={`flex items-start gap-3 py-2.5 px-3 rounded-lg transition-colors ${item.passed ? 'bg-emerald-50/50' : 'bg-red-50/30'}`}
      data-testid={`check-item-${item.id}`}>
      <div className="flex-shrink-0 mt-0.5">
        {item.passed
          ? <CheckCircle2 className="h-4.5 w-4.5 text-emerald-500" />
          : <XCircle className="h-4.5 w-4.5 text-red-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Icon className="h-3.5 w-3.5 text-muted-foreground" />
          <span className={`text-sm font-medium ${item.passed ? 'text-emerald-700' : 'text-foreground'}`}>{item.label}</span>
          {item.priority === 'high' && !item.passed && (
            <Badge className="bg-red-100 text-red-600 border-red-200 text-[9px]">Required</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">{item.detail}</p>
      </div>
      {showLink && !item.passed && item.category && (
        <Link to={CATEGORY_LINKS[item.category] || '/dashboard'}
          className="text-[10px] text-[#0F392B] hover:underline flex items-center gap-0.5 flex-shrink-0 mt-1">
          Fix <ArrowRight className="h-2.5 w-2.5" />
        </Link>
      )}
    </div>
  );
}

function SchoolChecklistCard({ school }) {
  const [open, setOpen] = useState(false);
  const allPassed = school.pct === 100;
  const typeColors = {
    reach: 'bg-red-50 text-red-600 border-red-200',
    target: 'bg-blue-50 text-blue-600 border-blue-200',
    safety: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  };

  return (
    <Card className={`border transition-all ${allPassed ? 'border-emerald-200' : 'border-[#E2E2E2]'}`}
      data-testid={`school-checklist-${school.school_id}`}>
      <CardContent className="p-0">
        <button onClick={() => setOpen(!open)}
          className="w-full flex items-center gap-3 p-3.5 text-left hover:bg-[#F9F9F7] transition-colors rounded-lg">
          <div className="flex-shrink-0">
            {allPassed
              ? <CheckCircle2 className="h-5 w-5 text-emerald-500" />
              : <AlertTriangle className="h-5 w-5 text-amber-500" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium truncate">{school.school_name}</span>
              <Badge variant="outline" className={`text-[9px] capitalize ${typeColors[school.college_type] || ''}`}>
                {school.college_type}
              </Badge>
            </div>
            <p className="text-xs text-muted-foreground">{school.passed}/{school.total} complete</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-xs font-semibold ${allPassed ? 'text-emerald-600' : 'text-muted-foreground'}`}>{school.pct}%</span>
            {open ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
          </div>
        </button>
        {open && (
          <div className="px-3.5 pb-3.5 space-y-1 border-t border-[#F2F2F0]">
            {school.items.map(item => (
              <div key={item.id} className="flex items-center gap-2.5 py-1.5">
                {item.passed
                  ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 flex-shrink-0" />
                  : <XCircle className="h-3.5 w-3.5 text-red-400 flex-shrink-0" />}
                <span className={`text-xs ${item.passed ? 'text-emerald-700' : 'text-foreground'}`}>{item.label}</span>
                <span className="text-[10px] text-muted-foreground ml-auto">{item.detail}</span>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default function ChecklistPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.getChecklist()
      .then(setData)
      .catch(() => toast.error('Failed to load checklist'))
      .finally(() => setLoading(false));
  }, []);

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const blob = await api.downloadChecklistPdf();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Submission_Checklist.pdf';
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Checklist PDF downloaded');
    } catch {
      toast.error('PDF download failed');
    } finally {
      setDownloading(false);
    }
  };

  if (loading) return (
    <div className="max-w-3xl mx-auto space-y-4 animate-pulse" data-testid="checklist-loading">
      <div className="h-32 bg-[#E2E2E2] rounded-xl" />
      {[1,2,3,4].map(i => <div key={i} className="h-14 bg-[#E2E2E2] rounded-xl" />)}
    </div>
  );

  if (!data) return null;

  const style = READINESS_STYLES[data.readiness_color] || READINESS_STYLES.red;
  const failedHigh = data.global_checklist.items.filter(i => !i.passed && i.priority === 'high');
  const failedMedium = data.global_checklist.items.filter(i => !i.passed && i.priority === 'medium');
  const passedItems = data.global_checklist.items.filter(i => i.passed);

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="checklist-page">
      {/* Header */}
      <div className="flex items-center gap-5">
        <div className="relative flex-shrink-0">
          <ReadinessRing pct={data.overall_pct} size={88} stroke={6} color={style.ring} />
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className="text-xl font-bold">{data.overall_pct}%</span>
          </div>
        </div>
        <div className="flex-1">
          <h1 className="text-2xl font-medium tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}
            data-testid="checklist-title">
            Submission Checklist
          </h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={`${style.bg} ${style.text} ${style.border} text-xs`} data-testid="readiness-badge">
              {data.readiness_label}
            </Badge>
            <span className="text-xs text-muted-foreground">
              {data.summary.total_passed}/{data.summary.total_items} items complete
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-1.5">
            {data.overall_pct >= 90
              ? "Your application looks strong. Review the items below for a final check."
              : data.overall_pct >= 70
              ? "Almost there! Complete the remaining items to strengthen your application."
              : data.overall_pct >= 40
              ? "Good progress. Focus on the required items first."
              : "Let's build your application step by step. Start with the required items below."}
          </p>
        </div>
        <Button onClick={handleDownloadPdf} disabled={downloading} variant="outline"
          className="h-9 text-xs gap-1.5 flex-shrink-0" data-testid="download-checklist-pdf">
          {downloading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
          Export PDF
        </Button>
      </div>

      {/* Required Items (failed high priority) */}
      {failedHigh.length > 0 && (
        <div className="space-y-2" data-testid="required-items-section">
          <h2 className="text-sm font-semibold text-red-600 uppercase tracking-wide flex items-center gap-1.5"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            <AlertTriangle className="h-3.5 w-3.5" /> Required ({failedHigh.length})
          </h2>
          <Card className="border-red-200/60">
            <CardContent className="p-2 space-y-0.5">
              {failedHigh.map(item => <CheckItem key={item.id} item={item} />)}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Recommended Items (failed medium priority) */}
      {failedMedium.length > 0 && (
        <div className="space-y-2" data-testid="recommended-items-section">
          <h2 className="text-sm font-semibold text-amber-600 uppercase tracking-wide flex items-center gap-1.5"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            <Sparkles className="h-3.5 w-3.5" /> Recommended ({failedMedium.length})
          </h2>
          <Card className="border-amber-200/60">
            <CardContent className="p-2 space-y-0.5">
              {failedMedium.map(item => <CheckItem key={item.id} item={item} />)}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Completed Items */}
      {passedItems.length > 0 && (
        <div className="space-y-2" data-testid="completed-items-section">
          <h2 className="text-sm font-semibold text-emerald-600 uppercase tracking-wide flex items-center gap-1.5"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            <CheckCircle2 className="h-3.5 w-3.5" /> Complete ({passedItems.length})
          </h2>
          <Card className="border-emerald-200/40">
            <CardContent className="p-2 space-y-0.5">
              {passedItems.map(item => <CheckItem key={item.id} item={item} showLink={false} />)}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Per-School Checklists */}
      {data.school_checklists.length > 0 && (
        <div className="space-y-2" data-testid="school-checklists-section">
          <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide"
            style={{ fontFamily: "'Playfair Display', serif" }}>
            Per-School Review ({data.school_checklists.length})
          </h2>
          <div className="space-y-2">
            {data.school_checklists.map(school => (
              <SchoolChecklistCard key={school.school_id} school={school} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state for no schools */}
      {data.school_checklists.length === 0 && (
        <Card className="border-dashed border-[#E2E2E2]">
          <CardContent className="p-6 text-center">
            <GraduationCap className="h-10 w-10 text-muted-foreground/20 mx-auto mb-2" strokeWidth={1.5} />
            <p className="text-sm text-muted-foreground">No schools added yet</p>
            <Link to="/colleges" className="text-xs text-[#0F392B] hover:underline mt-1 inline-block">
              Add schools to see per-school checklist
            </Link>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
